import { FetchError } from "../types";
import { TokenManager } from "./tokenManager";
import { TokenManagerInstance } from "./tokenManagerInstance";
import { dpopLog, dpopWarn } from "./dpopLogger";

// Cached DPoP key pair - generated once and reused across requests
let cachedDPoPKeyPair: CryptoKeyPair | null = null;

// Per-origin DPoP nonce store — populated from DPoP-Nonce response headers (RFC 9449 §8).
// Keyed by origin (e.g. "https://enaep.dev27.npdng.org") so the correct nonce is sent
// to the correct server in multi-origin scenarios.
const dpopNonceStore = new Map<string, string>();

/**
 * Store a DPoP nonce received from a server response for a given origin.
 * Exported so tokenManager.requestToken can also store nonces from token endpoint responses.
 */
export function storeDPoPNonce(origin: string, nonce: string): void {
  dpopNonceStore.set(origin, nonce);
  dpopLog('[DPoP] Nonce stored for origin:', origin);
}

/**
 * Retrieve the current DPoP nonce for a given origin, if one has been received.
 */
export function getDPoPNonce(origin: string): string | undefined {
  return dpopNonceStore.get(origin);
}

/**
 * Fetch with timeout helper - shared by both fetchService and secureFetchService
 */
function fetchWithTimeout(url: string, options: RequestInit, timeout: number): Promise<Response> {
  const controller = new AbortController();
  const { signal } = controller;
  const fetchOptions = { ...options, signal };
  // Start the fetch request
  const fetchPromise = fetch(url, fetchOptions);
  // Set up the timeout
  const timeoutPromise = new Promise<Response>((_, reject) =>
    setTimeout(() => {
      controller.abort();
      reject(new Error('Request timed out'));
    }, timeout)
  );
  // Return a race between the fetch and the timeout
  return Promise.race([fetchPromise, timeoutPromise]);
}

export async function fetchService<T>(
    url: string,
    options: RequestInit = {},
    retries: number = 3,
    timeout = 15000,
    delay: number = 1000
  ): Promise<Response> {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const response = await fetchWithTimeout(url, options, timeout);
        
        if (!response.ok) {
          const errorData = await response.json();
          const errorObj:FetchError = {errorType: "API error",response: response,details: `${response.status} 
          ${response.statusText}. Message: ${errorData.message || 'Unknown error'}`}
          throw new Error("API error",{cause: errorObj});
        }
  
        return  response;
      } catch (error: any) {
        console.log(error)
        if (attempt === retries) {
          if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
            const errorObj:FetchError = navigator.onLine ? 
            {errorType: "API error",details: `${error.name} . Message: ${error.message || 'Unknown error'}`}:
           {errorType: "Network error",details: `${error.name} . Message: ${error.message || 'Unknown error'}`}
            throw new Error(error.name,{cause: errorObj});
          }
          throw error;
        } else {
          await new Promise(res => setTimeout(res, delay));
        }
      }
    }
    // Fallback return in case all retries fail (shouldn't reach this point)
    const errorObj:FetchError = {errorType: "Unexpected error",details: `Unexpected error . Message: All retries failed`}         
    throw new Error("Unexpected error",{cause: errorObj});
  }

/**
 * Generates a DPoP proof JWT token
 * @param httpMethod - HTTP method (GET, POST, etc.)
 * @param httpUri - The full URI of the request
 * @param keyPair - CryptoKeyPair for signing the DPoP proof
 * @param accessToken - Optional access token to bind to the proof
 * @returns Base64URL-encoded DPoP proof JWT
 */
export async function generateDPoPProof(
  httpMethod: string,
  httpUri: string,
  keyPair: CryptoKeyPair,
  accessToken?: string,
  nonce?: string  // RFC 9449 §8 — server-provided nonce, included when server enforces nonce binding
): Promise<string> {
  // Generate JWK thumbprint for the public key
  const publicKeyJwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
  
  // Create JWT header
  const header = {
    typ: 'dpop+jwt',
    alg: 'ES256',
    jwk: publicKeyJwk
  };

  // Create JWT payload
  // exp = iat + 120 matches the server-enforced validity window (RFC 9449 §4.2)
  const iat = Math.floor(Date.now() / 1000);
  const payload: any = {
    jti: crypto.randomUUID(),
    htm: httpMethod,
    htu: httpUri,
    iat,
    exp: iat + 120,
    // nonce is included when the server has previously provided one via DPoP-Nonce header
    ...(nonce !== undefined && { nonce })
  };

  // Include access token hash if provided
  if (accessToken) {
    const tokenHash = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(accessToken)
    );
    payload.ath = base64UrlEncode(new Uint8Array(tokenHash));
  }

  // Encode header and payload
  const encodedHeader = base64UrlEncode(
    new TextEncoder().encode(JSON.stringify(header))
  );
  const encodedPayload = base64UrlEncode(
    new TextEncoder().encode(JSON.stringify(payload))
  );

  // Sign the JWT
  const signatureInput = `${encodedHeader}.${encodedPayload}`;
  const signature = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    keyPair.privateKey,
    new TextEncoder().encode(signatureInput)
  );

  const encodedSignature = base64UrlEncode(new Uint8Array(signature));

  return `${encodedHeader}.${encodedPayload}.${encodedSignature}`;
}

/**
 * Base64URL encode a Uint8Array (RFC 4648 §5)
 *
 * Avoids the spread-into-Function-args pattern (`String.fromCharCode(...data)`)
 * which exceeds the JS call stack for large inputs (e.g. signatures, public key JWKs).
 * Uses a for-loop to build the binary string one character at a time instead.
 */
function base64UrlEncode(data: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Generate or retrieve a DPoP key pair (cached for reuse)
 * Checks the in-memory cache first, then delegates to TokenManager to load from IndexDB,
 * and generates a new pair only if neither has one.
 * The key pair is generated once and reused across all requests per DPoP specification.
 */
async function getDPoPKeyPair(): Promise<CryptoKeyPair> {
  if (!cachedDPoPKeyPair) {
    // Try to restore key pair from IndexDB via TokenManager (persists across page reloads)
    const stored = await TokenManager.loadKeyPair();
    if (stored) {
      cachedDPoPKeyPair = stored;
      return cachedDPoPKeyPair;
    }
    // No stored key pair found — generate a fresh one.
    // Step 1: generate a temporary extractable key pair — needed only so we can
    // export the private key JWK once and immediately re-import it as non-extractable.
    const tempKeyPair = await crypto.subtle.generateKey(
      { name: 'ECDSA', namedCurve: 'P-256' },
      true, // temporary: extractable only to allow the re-import step below
      ['sign', 'verify']
    );

    // Persist to IndexDB using the still-extractable tempKeyPair before locking it.
    await TokenManager.saveKeyPair(tempKeyPair);

    // Step 2: re-import the private key as non-extractable (RFC 9449).
    // extractable: false prevents any future exportKey() call on the private key.
    const privateJwk = await crypto.subtle.exportKey('jwk', tempKeyPair.privateKey);
    const nonExtractablePrivateKey = await crypto.subtle.importKey(
      'jwk',
      privateJwk,
      { name: 'ECDSA', namedCurve: 'P-256' },
      false, // non-extractable — private key is now locked in the secure context
      ['sign']
    );

    cachedDPoPKeyPair = {
      privateKey: nonExtractablePrivateKey,
      publicKey: tempKeyPair.publicKey // remains extractable for DPoP proof embedding
    };
  }
  return cachedDPoPKeyPair;
}

/**
 * Pre-initialize the DPoP key pair
 * Optional - called during app initialization to avoid delay on first API call
 * If not called, key pair will be generated lazily on first secureFetchService() call
 * 
 * @returns Promise that resolves when key pair is generated
 * @example
 * // In app initialization
 * await initializeDPoPKeyPair();
 */
export async function initializeDPoPKeyPair(): Promise<void> {
  await getDPoPKeyPair();
  dpopLog('[DPoP] Key pair pre-generated and cached');
}

/**
 * Secure fetch service with DPoP (Demonstrating Proof-of-Possession) support
 * 
 * Automatically uses singleton TokenManager if initialized, otherwise works in DPoP-only mode
 * Same parameters as fetchService() for simplicity
 * 
 * @param url - The URL to fetch
 * @param options - RequestInit options
 * @param retries - Number of retry attempts (default: 3)
 * @param timeout - Request timeout in milliseconds (default: 15000)
 * @param delay - Delay between retries in milliseconds (default: 1000)
 * @returns Promise<Response>
 * 
 * @example
 * // Automatically uses TokenManager singleton if initialized, or DPoP-only
 * const response = await secureFetchService('/api/data', { method: 'GET' });
 * 
 * @example
 * // POST with body
 * const response = await secureFetchService('/api/data', {
 *   method: 'POST',
 *   headers: { 'Content-Type': 'application/json' },
 *   body: JSON.stringify({ key: 'value' })
 * });
 */
export async function secureFetchService<T>(
  url: string,
  options: RequestInit = {},
  retries: number = 3,
  timeout = 15000,
  delay: number = 1000
): Promise<Response> {
  let hasRetriedOnAuthFailure = false;
  let hasRetriedForNonce = false;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      // Get or generate DPoP key pair (cached after first generation)
      const keyPair = await getDPoPKeyPair();

      // Get access token from TokenManager singleton if initialized
      let accessToken: string | null = null;
      if (TokenManagerInstance.isTokenManagerInitialized()) {
        const tokenManager = TokenManagerInstance.getTokenManagerConfig();
        accessToken = await TokenManager.getValidToken(tokenManager, keyPair);
      } else {
        // else: DPoP-only mode (no tokens)
        dpopWarn('[secureFetchService] Using DPoP-only mode.');
      }

      // Parse URL to get the URI for DPoP proof
      // htu must be scheme + host + path only — no query string or fragment (RFC 9449 §4.2)
      const urlObj = new URL(url);
      const httpUri = `${urlObj.origin}${urlObj.pathname}`;
      const httpMethod = (options.method || 'GET').toUpperCase();

      // Look up any nonce previously provided by this server (RFC 9449 §8)
      const nonce = getDPoPNonce(urlObj.origin);

      // Generate fresh DPoP proof for this specific request
      const dpopProof = await generateDPoPProof(
        httpMethod,
        httpUri,
        keyPair,
        accessToken || undefined,
        nonce
      );

      // Add DPoP header to the request
      const secureOptions: RequestInit = {
        ...options,
        headers: {
          ...options.headers,
          'DPoP': dpopProof,
          ...(accessToken && { 'Authorization': `DPoP ${accessToken}` })
        }
      };

      const response = await fetchWithTimeout(url, secureOptions, timeout);

      // RFC 9449 §8 — always store any DPoP-Nonce the server returns, whether the
      // request succeeded or failed. The next iteration picks it up automatically.
      const serverNonce = response.headers.get('DPoP-Nonce');
      if (serverNonce) {
        storeDPoPNonce(urlObj.origin, serverNonce);
        dpopLog('[secureFetchService] DPoP-Nonce received and stored for', urlObj.origin);
      }

      // If the server returned an error with a nonce (e.g. use_dpop_nonce),
      // retry once — the nonce is now stored and will be included in the next proof.
      if (!response.ok && serverNonce && !hasRetriedForNonce) {
        hasRetriedForNonce = true;
        dpopWarn('[secureFetchService] DPoP-Nonce received with error response, retrying with nonce');
        attempt--;
        continue;
      }

      // Handle auth failures — force a fresh token and retry once.
      // The API Gateway Lambda authorizer returns 403 (not 401) for all DPoP/token
      // rejections. A 401 is included as a fallback for other upstreams.
      if (
        (response.status === 403 || response.status === 401) &&
        TokenManagerInstance.isTokenManagerInitialized() &&
        !hasRetriedOnAuthFailure
      ) {
        dpopWarn(`[secureFetchService] Received ${response.status}, clearing token and retrying with fresh token`);
        hasRetriedOnAuthFailure = true;
        await TokenManager.clearTokens(); // force re-acquisition on next getValidToken() call
        attempt--;
        continue;
      }

      if (!response.ok) {
        const errorData = await response.json();
        const errorObj: FetchError = {
          errorType: "API error",
          response: response,
          details: `${response.status} ${response.statusText}. Message: ${errorData.message || 'Unknown error'}`
        }
        throw new Error("API error", { cause: errorObj });
      }

      return response;
    } catch (error: any) {
      console.error(error);
      if (attempt === retries) {
        if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
          const errorObj: FetchError = navigator.onLine ?
            { errorType: "API error", details: `${error.name} . Message: ${error.message || 'Unknown error'}` } :
            { errorType: "Network error", details: `${error.name} . Message: ${error.message || 'Unknown error'}` }
          throw new Error(error.name, { cause: errorObj });
        }
        throw error;
      } else {
        await new Promise(res => setTimeout(res, delay));
      }
    }
  }
  // Fallback return in case all retries fail (shouldn't reach this point)
  const errorObj: FetchError = { errorType: "Unexpected error", details: `Unexpected error . Message: All retries failed` }
  throw new Error("Unexpected error", { cause: errorObj });
}
  